// eslint-disable-next-line no-restricted-imports
import {fire, on} from 'delegated-events'
// eslint-disable-next-line no-restricted-imports
import {observe} from '@github/selector-observer'
import {requestSubmit} from '@github-ui/form-utils'
import {hasFile} from '../behaviors/document-dropzone'
import {onInput} from '../onfocus'
import {toggleDetails} from '../behaviors/details-element'

on('click', '.js-bulk-sponsorship-import-csv-button', function () {
  const fileInput = document.getElementById('bulk-sponsorship-import-csv')
  if (!fileInput) return
  ;(fileInput as HTMLInputElement).value = ''
  fileInput.click()
})

on('change', '.js-bulk-sponsorship-import-csv', function (event) {
  const input = event.target as HTMLInputElement
  const form = input.form!
  requestSubmit(form)
})

on('document:drop', '.js-bulk-sponsorship-import-dropzone', async function (event) {
  const fileInput = document.querySelector<HTMLInputElement>('.js-bulk-sponsorship-import-csv')
  if (!fileInput) return

  const transfer = event.detail.transfer as DataTransfer
  fileInput.files = transfer.files

  fire(fileInput, 'change')
})

let dragging: number | null = null

function onBulkSponsorshipDropzoneDragEnter(event: DragEvent) {
  const currentTarget = event.currentTarget as Element
  if (dragging) {
    window.clearTimeout(dragging)
  }
  dragging = window.setTimeout(() => currentTarget.classList.remove('bulk-sponsor-dragover'), 200)

  const transfer = event.dataTransfer
  if (!transfer || !hasFile(transfer)) return

  transfer.dropEffect = 'copy'
  currentTarget.classList.add('bulk-sponsor-dragover')

  event.stopPropagation()
  event.preventDefault()
}

function onBulkSponsorshipDropzoneDragLeave(event: DragEvent) {
  // Ignore leave events from children to prevent flicker.
  if (!(event.target instanceof Element)) return

  if (event.target.classList.contains('js-bulk-sponsorship-import-dropzone')) {
    const currentTarget = event.currentTarget as Element
    currentTarget.classList.remove('bulk-sponsor-dragover')
  }
}

observe('.js-bulk-sponsorship-import-dropzone', {
  constructor: HTMLElement,
  add(el) {
    el.addEventListener('dragenter', onBulkSponsorshipDropzoneDragEnter)
    el.addEventListener('dragover', onBulkSponsorshipDropzoneDragEnter)
    el.addEventListener('dragleave', onBulkSponsorshipDropzoneDragLeave)
  },
  remove(el) {
    el.removeEventListener('dragenter', onBulkSponsorshipDropzoneDragEnter)
    el.removeEventListener('dragover', onBulkSponsorshipDropzoneDragEnter)
    el.removeEventListener('dragleave', onBulkSponsorshipDropzoneDragLeave)
  },
})

function getContainingBulkSponsorshipRow(element: HTMLElement) {
  return element.closest<HTMLElement>('.js-bulk-sponsorship-row')
}

function updateTotalBulkSponsorshipsCount(checkedCheckboxes: HTMLInputElement[], form: HTMLFormElement) {
  const totalCountEl = form.querySelector<HTMLElement>('.js-bulk-sponsorship-total-count')
  if (!totalCountEl) return

  const totalCount = checkedCheckboxes.length
  totalCountEl.textContent = `${totalCount}`
}

function updateTotalBulkSponsorshipsCountUnits(checkedCheckboxes: HTMLInputElement[], form: HTMLFormElement) {
  const unitsEl = form.querySelector<HTMLElement>('.js-bulk-sponsorship-total-count-units')
  if (!unitsEl) return

  const totalCount = checkedCheckboxes.length

  // "maintainers" then "organizations":
  const checkedUnits = [...new Set(checkedCheckboxes.map(c => c.getAttribute('data-unit')))].sort().reverse()

  if (totalCount < 1) {
    const defaultUnits = unitsEl.getAttribute('data-default-plural')
    unitsEl.textContent = defaultUnits // "0 maintainers or organizations"
  } else if (totalCount === 1) {
    // a single sponsorable is selected
    unitsEl.textContent = checkedUnits[0]! // "1 maintainer" or "1 organization"
  } else if (checkedUnits.length < 2) {
    // many of one kind of sponsorable is selected
    const singularUnits = checkedUnits[0]
    const pluralUnits = unitsEl.getAttribute(`data-${singularUnits}-plural`)
    unitsEl.textContent = pluralUnits // e.g., "2 maintainers", "2 organizations"
  } else {
    // many of many kinds of sponsorable are selected
    const joinText = unitsEl.getAttribute('data-unit-join') || ', '
    const pluralUnits = checkedUnits.map(singularUnits => unitsEl.getAttribute(`data-${singularUnits}-plural`))
    unitsEl.textContent = pluralUnits.join(joinText) // e.g., "2 maintainers and organizations"
  }

  if (unitsEl.textContent) {
    unitsEl.textContent = unitsEl.textContent + unitsEl.getAttribute('data-unit-suffix')
  }
}

function formatDollars(dollars: number) {
  const dollarUS = Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'})
  return dollarUS.format(dollars)
}

function updateTotalBulkSponsorshipAmount(checkedCheckboxes: HTMLInputElement[], form: HTMLFormElement) {
  const amounts = getAmountsFromCheckedRows(checkedCheckboxes)
  const totalDollars = amounts.reduce((sum, amount) => sum + amount, 0)
  const totalAmountEl = form.querySelector<HTMLElement>('.js-bulk-sponsorship-total-amount')!

  totalAmountEl.textContent = formatDollars(totalDollars)
}

function disableBulkSponsorshipSubmitIfNecessary(checkedCheckboxes: HTMLInputElement[], form: HTMLFormElement) {
  const submitButton = form.querySelector<HTMLButtonElement>('.js-bulk-sponsorship-submit-button')
  if (!submitButton) return

  const totalChecked = checkedCheckboxes.length
  submitButton.disabled = totalChecked < 2
}

function getCheckedBulkSponsorshipCheckboxes(container: HTMLElement) {
  const checkboxes = Array.from(container.querySelectorAll<HTMLInputElement>('.js-bulk-sponsorship-checkbox'))
  return checkboxes.filter(c => c.checked)
}

function toggleDisplayOfGlobalBulkSponsorshipAmountControl(
  checkedCheckboxes: HTMLInputElement[],
  form: HTMLFormElement,
) {
  const headerCell = form.querySelector<HTMLElement>('.js-bulk-sponsorship-amount-column-header')
  if (!headerCell) return

  const totalChecked = checkedCheckboxes.length
  const minAmountForGlobalControl = 2

  const columnHeaderTextEl = headerCell.querySelector<HTMLElement>(
    '.js-bulk-sponsorship-default-amount-column-header-text',
  )!
  columnHeaderTextEl.hidden = totalChecked >= minAmountForGlobalControl

  const amountControlDetails = headerCell.querySelector<HTMLElement>('.js-bulk-sponsorship-global-amount-details')!
  amountControlDetails.hidden = totalChecked < minAmountForGlobalControl
}

function updateBulkSponsorshipAmountInputAttributesBasedOnCheckbox(checkbox: HTMLInputElement) {
  const row = getContainingBulkSponsorshipRow(checkbox)
  if (!row) return

  const amountInput = row.querySelector<HTMLInputElement>('.js-bulk-sponsorship-amount')
  if (!amountInput) return

  if (checkbox.checked) {
    amountInput.setAttribute('required', 'required')

    const maxAmount = amountInput.getAttribute('data-max')
    if (maxAmount) {
      amountInput.setAttribute('max', maxAmount)
    }

    const minAmount = amountInput.getAttribute('data-min')
    if (minAmount) {
      amountInput.setAttribute('min', minAmount)
    }

    updateBulkSponsorshipAmountForAccountSwitcher(amountInput)
  } else {
    amountInput.removeAttribute('required')

    const maxAmount = amountInput.getAttribute('max')
    if (maxAmount) {
      amountInput.setAttribute('data-max', maxAmount)
      amountInput.removeAttribute('max')
    }

    const minAmount = amountInput.getAttribute('min')
    if (minAmount) {
      amountInput.setAttribute('data-min', minAmount)
      amountInput.removeAttribute('min')
    }

    removeBulkSponsorshipAmountForAccountSwitcher(amountInput)
  }
}

on('change', '.js-bulk-sponsorship-checkbox', event => {
  const checkbox = event.target as HTMLInputElement
  updateBulkSponsorshipAmountInputAttributesBasedOnCheckbox(checkbox)

  const form = checkbox.form
  if (!form) return

  const checkedCheckboxes = getCheckedBulkSponsorshipCheckboxes(form)
  updateTotalBulkSponsorshipAmount(checkedCheckboxes, form)
  updateTotalBulkSponsorshipsCount(checkedCheckboxes, form)
  updateTotalBulkSponsorshipsCountUnits(checkedCheckboxes, form)
  disableBulkSponsorshipSubmitIfNecessary(checkedCheckboxes, form)
  toggleDisplayOfGlobalBulkSponsorshipAmountControl(checkedCheckboxes, form)
})

function getBulkSponsorshipCheckboxInRow(row: HTMLElement) {
  return row.querySelector<HTMLInputElement>('.js-bulk-sponsorship-checkbox')
}

function toggleCheckedBulkSponsorshipCheckboxInRow(row: HTMLElement) {
  const checkbox = getBulkSponsorshipCheckboxInRow(row)
  if (!checkbox || checkbox.disabled) return

  checkbox.checked = !checkbox.checked
  fire(checkbox, 'change')
}

on('click', '.js-bulk-sponsorship-row', event => {
  const target = event.target as HTMLElement
  if (['A', 'INPUT'].includes(target.nodeName)) return true

  let row = target
  if (!row.classList.contains('js-bulk-sponsorship-row')) {
    row = getContainingBulkSponsorshipRow(target)!
  }

  toggleCheckedBulkSponsorshipCheckboxInRow(row)
})

function toggleDisabledBulkSponsorshipCheckboxInRow(row: HTMLElement, enable: boolean) {
  const checkbox = getBulkSponsorshipCheckboxInRow(row)
  if (!checkbox) return

  let shouldFire = false
  if (enable && !checkbox.hasAttribute('data-check-all-item')) {
    checkbox.setAttribute('data-check-all-item', '')
  }

  if (!enable && checkbox.checked) {
    checkbox.checked = false
    shouldFire = true
  }

  checkbox.disabled = !enable

  if (!enable && checkbox.hasAttribute('data-check-all-item')) {
    checkbox.removeAttribute('data-check-all-item')
  }

  if (enable && !checkbox.checked) {
    checkbox.checked = true
    shouldFire = true
  }

  if (shouldFire) {
    fire(checkbox, 'change')
  }
}

function toggleDisplayOfBulkSponsorshipCorrectableError(row: HTMLElement, isValid: boolean) {
  const error = row.querySelector<HTMLElement>('.js-bulk-sponsorship-correctable-error')
  if (!error) return

  error.hidden = isValid
}

function isAmountValid(row: HTMLElement, input: HTMLInputElement) {
  // Ensure the `min`, `max`, and `required` attributes on the input are satisfied first:
  if (input.value === '' || !input.checkValidity()) return false

  // Parse integers using base 10; see
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/parseInt#syntax
  const radix = 10

  const amount = parseInt(input.value, radix)

  if (row.hasAttribute('data-min-custom-amount')) {
    const minCustomAmount = parseInt(row.getAttribute('data-min-custom-amount')!, radix)
    if (amount >= minCustomAmount) return true
  } else {
    // No custom minimum amount, so all amounts lower than the original given amount are valid
    return true
  }

  // Get a string of comma-separated integer dollar amounts that represent the published tiers that are available
  // for the sponsorable in this row, e.g., "5,10,15" to represent a $5, $10, and $15 tier.
  const validLowerAmountsStr = row.getAttribute('data-valid-amounts') || ''

  // Convert a string like "5,10,15" to an array of integers like [5, 10, 15].
  const validLowerAmounts = validLowerAmountsStr.split(',').map(str => parseInt(str, radix))
  return validLowerAmounts.includes(amount)
}

function toggleErroredFormGroupForBulkSponsorshipAmount(input: HTMLInputElement, isValid: boolean) {
  const formGroup = input.closest<HTMLElement>('.form-group')
  if (!formGroup) return

  formGroup.classList.toggle('errored', !isValid)
}

function handleBulkSponsorshipAmountChange(input: HTMLInputElement) {
  const row = getContainingBulkSponsorshipRow(input)
  if (row) {
    const checkbox = getBulkSponsorshipCheckboxInRow(row)
    if (checkbox) {
      updateBulkSponsorshipAmountInputAttributesBasedOnCheckbox(checkbox)
    }

    const isValid = isAmountValid(row, input)
    toggleDisabledBulkSponsorshipCheckboxInRow(row, isValid)
    toggleDisplayOfBulkSponsorshipCorrectableError(row, isValid)
    toggleErroredFormGroupForBulkSponsorshipAmount(input, isValid)
  }

  const form = input.form
  if (form) {
    const checkedCheckboxes = getCheckedBulkSponsorshipCheckboxes(form)
    updateTotalBulkSponsorshipAmount(checkedCheckboxes, form)
  }

  updateBulkSponsorshipAmountForAccountSwitcher(input)
}

function getAmountsFromCheckedRows(checkedCheckboxes: HTMLInputElement[]) {
  const checkedRows = checkedCheckboxes.map(getContainingBulkSponsorshipRow)
  const amountInputs = checkedRows.map(row => row?.querySelector<HTMLInputElement>('.js-bulk-sponsorship-amount'))

  // Parse integers using base 10; see
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/parseInt#syntax
  const radix = 10

  const amounts = amountInputs
    .map(input => input?.value)
    .filter(v => v)
    .map(v => parseInt(v || '0', radix))

  return amounts
}

function updateBulkSponsorshipAmountForAccountSwitcher(input: HTMLInputElement) {
  const accountSwitcherForm = getAccountSwitcherForm(input)
  if (!accountSwitcherForm) return

  const accountSwitcherAmountField = accountSwitcherForm.querySelector<HTMLInputElement>(`input[name='${input.name}']`)
  if (accountSwitcherAmountField) {
    accountSwitcherAmountField.value = input.value
  } else {
    const newAmountField = getNewBulkSponsorshipHiddenInputFor(input)
    accountSwitcherForm.appendChild(newAmountField)
  }
}

function getNewBulkSponsorshipHiddenInputFor(inputToCopy: HTMLInputElement) {
  const amountFieldToInsert = document.createElement('input')
  amountFieldToInsert.setAttribute('type', 'hidden')
  amountFieldToInsert.setAttribute('name', inputToCopy.name)
  amountFieldToInsert.setAttribute('value', inputToCopy.value)
  amountFieldToInsert.setAttribute('autocomplete', 'off')
  return amountFieldToInsert
}

function removeBulkSponsorshipAmountForAccountSwitcher(input: HTMLInputElement) {
  const accountSwitcherForm = getAccountSwitcherForm(input)
  if (!accountSwitcherForm) return

  const accountSwitcherAmountField = accountSwitcherForm.querySelector<HTMLInputElement>(`input[name='${input.name}']`)
  accountSwitcherAmountField?.remove()
}

function getAccountSwitcherForm(input: HTMLInputElement) {
  const container = input.closest<HTMLElement>('.js-bulk-sponsorship-import-container')
  return container?.querySelector<HTMLFormElement>('.js-account-switcher-item-form')
}

on('change', '.js-bulk-sponsorship-amount', event => {
  const input = event.target as HTMLInputElement
  handleBulkSponsorshipAmountChange(input)
})

onInput('.js-bulk-sponsorship-amount', event => {
  const input = event.target as HTMLInputElement
  handleBulkSponsorshipAmountChange(input)
})

function handleGlobalBulkSponsorshipAmountChange(input: HTMLInputElement) {
  if (input.value === '' || !input.checkValidity()) return

  const container = input.closest<HTMLElement>('.js-bulk-sponsorships-container')
  if (!container) return

  // Parse integers using base 10; see
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/parseInt#syntax
  const radix = 10

  const amount = parseInt(input.value, radix)
  const individualAmountInputs = container.querySelectorAll<HTMLInputElement>('.js-bulk-sponsorship-amount')

  for (const individualAmountInput of individualAmountInputs) {
    const row = getContainingBulkSponsorshipRow(individualAmountInput)
    if (!row) continue

    const checkbox = getBulkSponsorshipCheckboxInRow(row)
    if (!checkbox) continue
    if (checkbox.disabled || !checkbox.checked) continue

    individualAmountInput.value = amount.toString()
    fire(individualAmountInput, 'change')
  }
}

function getBulkSponsorshipGlobalAmountInput(container: Element) {
  return container.querySelector<HTMLInputElement>('.js-bulk-sponsorship-global-amount')
}

on('submit', '.js-bulk-sponsorship-global-amount-form', event => {
  event.preventDefault()

  const container = document.querySelector<HTMLElement>('.js-bulk-sponsorships-container')
  if (!container) return

  const input = getBulkSponsorshipGlobalAmountInput(container)!
  handleGlobalBulkSponsorshipAmountChange(input)
  toggleBulkSponsorshipAmountDetails(input)
})

function toggleBulkSponsorshipAmountDetails(elementInDetails: HTMLElement) {
  const details = elementInDetails.closest<HTMLElement>('.js-bulk-sponsorship-global-amount-details')
  if (details) {
    toggleDetails(details)
  }
}

function focusBulkSponsorshipGlobalAmountInput(container: Element) {
  const globalAmountInput = getBulkSponsorshipGlobalAmountInput(container)
  if (globalAmountInput) {
    globalAmountInput.focus()
  }
}

observe('.js-bulk-sponsorship-global-amount-details', details => {
  details.addEventListener('toggle', () => {
    if ((details as HTMLDetailsElement).open) {
      focusBulkSponsorshipGlobalAmountInput(details)
    }
  })
})
